import { Grid, SegmentedControl, Text } from '@mantine/core';
import { facebookAudienceOptions } from '../../constants/Rules/facebookAudienceRules';
import { googleAudienceOptions } from '../../constants/Rules/googleUserListRules';
import { capitalizeFirstChar } from '../../helpers/utils';
import Spinner from '../Spinner';
import CustomAudienceRules from './CustomAudienceRules';
import ConversionBasedAudience from './Google/ConversionBasedAudience';
import SegmentSelector from './SegmentSelector';

const AudienceRules = ({ segment, updateSegment, readOnly }) => {
  if (!segment) {
    return <Spinner />;
  }
  const label = capitalizeFirstChar(segment.scope);

  let rules;
  if (segment.externalScope === 'audienceRules') {
    rules = (
      <Grid data-tour="segmentPageRules">
        <Grid.Col span={{ base: 12, sm: 3 }}>Rules</Grid.Col>
        <Grid.Col span={{ base: 12, sm: 9 }}>
          <CustomAudienceRules segment={segment} updateSegment={updateSegment} readOnly={readOnly} />
        </Grid.Col>
      </Grid>
    );
  } else if (segment.externalScope === 'audienceLookalikes') {
    rules = segment.scope === 'facebook' && (
      <>
        <SegmentSelector
          sizeTitle={3}
          sizeBody={9}
          description="This determines the set of users that we find lookalikes for"
          idMapper={(segment) => segment.externalId}
          segmentId={segment.rules[0].originAudienceId}
          onSelect={(lookalikeSegment) =>
            updateSegment({
              ...segment,
              rules: [
                {
                  ...segment.rules[0],
                  originAudienceId: lookalikeSegment.externalId,
                },
              ],
            })
          }
          campaignScope="transactional"
          scope={segment.scope}
          title="Origin segment"
          disabled={readOnly}
        />
      </>
    );
  } else if (segment.externalScope === 'conversionActions') {
    rules = (
      <Grid>
        <Grid.Col span={{ base: 12, sm: 3 }}>Conversion Actions:</Grid.Col>
        <Grid.Col span={{ base: 12, sm: 9 }}>
          <ConversionBasedAudience segment={segment} updateSegment={updateSegment} readOnly={readOnly} />
        </Grid.Col>
      </Grid>
    );
  }

  const options =
    segment.scope === 'facebook' ? facebookAudienceOptions : segment.scope === 'google' ? googleAudienceOptions : [];

  return (
    <>
      <Grid>
        <Grid.Col span={{ base: 12, sm: 3 }}>
          <Text size="sm" fw={500}>
            {label} Scope*
          </Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 9 }}>
          <SegmentedControl
            value={segment.externalScope ?? ''}
            disabled={!!segment.id}
            onChange={(externalScope) => {
              const modifiedSegment = JSON.parse(JSON.stringify(segment));
              modifiedSegment.externalScope = externalScope;
              if (externalScope === 'audienceRules' || externalScope === 'conversionActions') {
                modifiedSegment.rules = [];
              } else {
                modifiedSegment.rules = [{ lookalikeType: segment.scope === 'facebook' ? 'reach' : 'BALANCED' }];
              }
              updateSegment(modifiedSegment);
            }}
            data={options.externalScope}
          />

          {segment.scope === 'facebook' ? (
            <Text size="sm" c="dimmed">
              With <strong>Audience rules</strong>, you can choose users based on their actions on your site. <br />
              Using <strong>Segment lookalikes</strong>, you can broaden an existing segment by adding similar users.{' '}
              <br />
              And <strong>Page fans lookalikes</strong> enables you to select individuals who have liked your page and
              extend it with similar users. <br />
            </Text>
          ) : (
            <Text size="sm" c="dimmed">
              With <strong>Audience Rules for Web Page Visitors</strong>, you can target users based on their
              interactions during web page visits. <br />
              <strong>Conversion Actions</strong> allow you to include users who visited pages with specific tags.{' '}
              <br />
              Using <strong>Segment Lookalikes</strong>, you can expand an existing segment by adding users with similar
              behaviors. <br />
            </Text>
          )}
        </Grid.Col>
      </Grid>
      {rules}
      {['audienceLookalikes', 'pageLikeLookalikes'].includes(segment.externalScope) && (
        <Grid>
          <Grid.Col span={{ base: 12, sm: 3 }}>
            <Text size="sm" fw={500}>
              Lookalike type
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 9 }}>
            <SegmentedControl
              value={segment.rules[0].lookalikeType ?? ''}
              disabled={!!segment.id}
              onChange={(lookalikeType) =>
                updateSegment({
                  ...segment,
                  rules: [{ ...segment.rules[0], lookalikeType }],
                })
              }
              data={options.lookalikeType}
            />

            <Text size="sm" c="dimmed">
              <strong>Reach</strong> aims to include a larger number of users, even those who are less similar. This
              option is ideal for expanding your audience. <br />
              On the other hand, <strong>Similarity</strong> only selects the most similar users.
            </Text>
          </Grid.Col>
        </Grid>
      )}
    </>
  );
};

export default AudienceRules;
